import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LockKey, EnvelopeSimple, SignIn, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import LanguageToggle from '@/components/layout/LanguageToggle';
import { toast } from 'sonner';

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get('next') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If the user is already authenticated, bounce to their destination
  useEffect(() => {
    if (user && typeof user === 'object') {
      navigate(nextUrl.startsWith('/login') ? '/' : nextUrl, { replace: true });
    }
  }, [user, nextUrl, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(t('login.success'));
      navigate(nextUrl.startsWith('/login') ? '/' : nextUrl, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t('login.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 bg-gradient-to-br from-violet-50 via-white to-blue-50 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-violet-200/40 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] bg-blue-200/40 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_d6ddacf3-4a07-466a-89d3-9a93989d8d3d/artifacts/u7tlxn82_image.png"
            alt="Sensbey"
            className="h-10 mx-auto mb-3"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Sensbey Web App</h1>
          <p className="text-xs text-zinc-500 mt-1 tracking-wide" data-testid="brand-tagline">統合業務プラットフォーム</p>
          <p className="text-sm text-zinc-500 mt-1">{t('login.subtitle')}</p>
          <div className="flex justify-center mt-3"><LanguageToggle /></div>
        </div>

        {/* Card */}
        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-7 space-y-5"
          data-testid="login-form"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-semibold text-zinc-700">{t('login.email')}</Label>
            <div className="relative">
              <EnvelopeSimple size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9 h-10"
                placeholder="you@sensbey.co.jp"
                data-testid="login-email"
                disabled={loading}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-semibold text-zinc-700">{t('login.password')}</Label>
            <div className="relative">
              <LockKey size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                id="password"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 pr-9 h-10"
                placeholder="••••••••"
                data-testid="login-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700 rounded"
                aria-label={showPw ? t('login.hidePw') : t('login.showPw')}
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
              data-testid="login-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="login-submit"
          >
            <SignIn size={16} weight="bold" className="mr-2" />
            {loading ? t('login.submitting') : t('login.submit')}
          </Button>

          <p className="text-[11px] text-center text-zinc-400">
            {t('login.forgot')}
          </p>
        </form>

        <p className="text-center text-[11px] text-zinc-400 mt-6">
          © 2026 株式会社センスビー
        </p>
      </div>
    </div>
  );
}
